import React, { useState, useEffect } from 'react';
import useProfileStore from '../../store/useProfileStore';

function Personal() {
  const { profile, getUserProfile, updateUserProfile, isLoading, message, clearMessage } = useProfileStore();
  
  const [editing, setEditing] = useState({
    first_name: false,
    last_name: false
  });
  
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [localError, setLocalError] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  const country = profile?.country;
  const countryValue = typeof country === 'string'
    ? country.trim()
    : country?.name || country?.code || '';
  let countryName = countryValue || 'N/A';
  if (/^[a-z]{2}$/i.test(countryValue)) {
    try {
      countryName = new Intl.DisplayNames(['en'], { type: 'region' })
        .of(countryValue.toUpperCase()) || countryValue;
    } catch {
      // Keep the API's country code if region names are unavailable.
    }
  }

  // Fetch user profile on component mount
  useEffect(() => {
    getUserProfile();
  }, [getUserProfile]);

  // Update form data when profile is loaded
  useEffect(() => {
    if (profile) {
      setFormData({
        username: profile.username || '',
        first_name: profile.first_name || '',
        last_name: profile.last_name || ''
      });
    }
  }, [profile]);

  // Clear messages when component unmounts
  useEffect(() => {
    return () => {
      // clearError(); // This line is removed as per the new_code
      clearMessage();
    };
  }, [clearMessage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Clear local error when user starts typing
    if (localError) {
      setLocalError(null);
    }
  };

  const handleEdit = (field) => {
    setEditing(prev => ({
      ...prev,
      [field]: true
    }));
    
    // Clear field-specific error when entering edit mode
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
    
    // Clear local error when entering edit mode
    if (localError) {
      setLocalError(null);
    }
  };

  const handleSave = async (field) => {
    try {
      setLocalLoading(true);
      setLocalError(null);
      await updateUserProfile(formData);
      setEditing(prev => ({
        ...prev,
        [field]: false
      }));
    } catch (error) {
      console.error('Profile update error:', error);
      // Handle field-specific errors
      if (error.username && field === 'username') {
        setFieldErrors(prev => ({
          ...prev,
          username: error.username.map(err => err.message).join(', ')
        }));
      }
      // Set local error for general errors
      if (error.detail) {
        setLocalError(error.detail);
      } else if (error.message) {
        setLocalError(error.message);
      }
    } finally {
      setLocalLoading(false);
    }
  };

  const handleCancel = (field) => {
    // Reset to original profile data
    if (profile) {
      setFormData(prev => ({
        ...prev,
        [field]: profile[field] || ''
      }));
    }
    setEditing(prev => ({
      ...prev,
      [field]: false
    }));
    
    // Clear field-specific error
    setFieldErrors(prev => ({
      ...prev,
      [field]: null
    }));
    
    // Clear local error
    setLocalError(null);
  };

  const handleSaveAll = async () => {
    try {
      setLocalLoading(true);
      setLocalError(null);
      await updateUserProfile(formData);
      // Exit edit mode for all fields
      setEditing({
        first_name: false,
        last_name: false
      });
      // Clear all field errors
      setFieldErrors({});
    } catch (error) {
      console.error('Profile update error:', error);
      // Handle field-specific errors
      if (error.username) {
        setFieldErrors(prev => ({
          ...prev,
          username: error.username.map(err => err.message).join(', ')
        }));
      }
      if (error.first_name) {
        setFieldErrors(prev => ({
          ...prev,
          first_name: error.first_name.map(err => err.message).join(', ')
        }));
      }
      if (error.last_name) {
        setFieldErrors(prev => ({
          ...prev,
          last_name: error.last_name.map(err => err.message).join(', ')
        }));
      }
      // Set local error for general errors
      if (error.detail) {
        setLocalError(error.detail);
      } else if (error.message) {
        setLocalError(error.message);
      }
    } finally {
      setLocalLoading(false);
    }
  };

  if (isLoading && !profile) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold  mb-6">Personal info</h2>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold  mb-6">Personal info</h2>
      
      {/* Message Display */}
      {message && (
        <div className="mb-4 p-3 rounded-md bg-green-100 text-green-700 border border-green-200">
          {message}
        </div>
      )}
      
      {/* Error Display */}
      {localError && (
        <div className="mb-4 p-3 rounded-md bg-red-100 text-red-700 border border-red-200">
          {localError}
        </div>
      )}
      
      {/* Field-specific errors summary */}
      {Object.keys(fieldErrors).some(key => fieldErrors[key]) && (
        <div className="mb-4 p-3 rounded-md bg-red-100 text-red-700 border border-red-200">
          <div className="text-sm font-medium mb-2">Please fix the following errors:</div>
          {Object.entries(fieldErrors).map(([field, error]) => 
            error ? (
              <div key={field} className="text-sm">
                <span className="font-medium capitalize">{field.replace('_', ' ')}:</span> {error}
              </div>
            ) : null
          )}
        </div>
      )}
      
      <div className="space-y-4">
        {/* Username */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <span className="text-white">Username:</span>
          <span className=" font-medium">{profile?.username || 'N/A'}</span>
        </div>
        
        {/* First Name */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <span className="text-white">First Name:</span>
          <div className="flex items-center space-x-2">
            {editing.first_name ? (
              <div className="flex flex-col items-end space-y-1">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={`px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      fieldErrors.first_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button 
                    onClick={() => handleSave('first_name')}
                    className="text-green-500 hover:text-green-600 text-sm"
                  >
                    ✓
                  </button>
                  <button 
                    onClick={() => handleCancel('first_name')}
                    className="text-red-500 hover:text-red-600 text-sm"
                  >
                    ✕
                  </button>
                </div>
                {fieldErrors.first_name && (
                  <span className="text-xs text-red-500">{fieldErrors.first_name}</span>
                )}
              </div>
            ) : (
              <>
                <span className=" font-medium">{profile?.first_name || 'N/A'}</span>
                <button 
                  onClick={() => handleEdit('first_name')}
                  className="text-blue-500 hover:text-blue-600"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Last Name */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <span className="text-white">Last Name:</span>
          <div className="flex items-center space-x-2">
            {editing.last_name ? (
              <div className="flex flex-col items-end space-y-1">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={`px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      fieldErrors.last_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <button 
                    onClick={() => handleSave('last_name')}
                    className="text-green-500 hover:text-green-600 text-sm"
                  >
                    ✓
                  </button>
                  <button 
                    onClick={() => handleCancel('last_name')}
                    className="text-red-500 hover:text-red-600 text-sm"
                  >
                    ✕
                  </button>
                </div>
                {fieldErrors.last_name && (
                  <span className="text-xs text-red-500">{fieldErrors.last_name}</span>
                )}
              </div>
            ) : (
              <>
                <span className=" font-medium">{profile?.last_name || 'N/A'}</span>
                <button 
                  onClick={() => handleEdit('last_name')}
                  className="text-blue-500 hover:text-blue-600"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Country/Region from the user's profile */}
        <div className="flex items-center justify-between py-3">
          <span className="text-white">Country/Region of residence:</span>
          <span className=" font-medium">{countryName}</span>
        </div>
        
        {/* Save Button */}
        <div className="pt-6 border-t border-gray-200">
          <button
            onClick={handleSaveAll}
            disabled={isLoading || localLoading}
            className={`w-full px-6 py-3 rounded-md font-black transition-colors shadow-md ${
              localLoading 
                ? 'bg-gray-400 cursor-not-allowed !text-white' 
                : 'bg-[#0E1696] hover:bg-[#0E1696] !text-white'
            }`}
            style={!localLoading ? { color: 'black' } : {}}
          >
            {localLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Personal;
